import { Pill, type PillSize, type PillVariant } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { type UnmetCriteriaCode } from "@src/appV2/redesign/Shift/types";
import { ShiftWindow } from "@src/appV2/Shifts/Shift/types";
import { useMemo } from "react";

interface WindowedOutPillProps {
  size?: PillSize;
  variant?: PillVariant;
  window: string;
  workerScore?: number;
  windowThreshold?: number;
  unmetCriteria?: UnmetCriteriaCode[];
  disabled?: boolean;
}

export function WindowedOutPill(props: WindowedOutPillProps) {
  const {
    size = "small",
    variant = "filled",
    window,
    workerScore,
    windowThreshold,
    unmetCriteria,
    disabled = true,
  } = props;

  const label = useMemo(() => {
    switch (window) {
      case ShiftWindow.CLIPBOARD_SCORE_FIRST:
      case ShiftWindow.CLIPBOARD_SCORE_SECOND: {
        return "High Scorers";
      }

      case ShiftWindow.RELIABLE_AND_FAMILIAR: {
        // Use backend-provided unmetCriteria when available, falling back to
        // score-based heuristic for backwards compatibility with older backends
        // that don't provide unmetCriteria (i.e. unmetCriteria is undefined).
        if (isDefined(unmetCriteria)) {
          const isBelowScore = unmetCriteria.includes("BELOW_SCORE_THRESHOLD");
          const isNotFamiliar = unmetCriteria.includes("NOT_FAMILIAR_WITH_WORKPLACE");

          if (isBelowScore) {
            return "High Scorers";
          }

          if (isNotFamiliar) {
            return "Regulars";
          }

          // Generic fallback when unmetCriteria is defined but contains no recognized codes
          // (e.g. empty array or unknown codes). Do not fall back to the heuristic.
          return "Priority Access";
        }

        // Heuristic fallback for older backends that don't provide unmetCriteria
        const meetsScoreRequirement =
          isDefined(workerScore) && isDefined(windowThreshold) && workerScore >= windowThreshold;

        if (meetsScoreRequirement) {
          return "Regulars";
        }

        return "High Scorers";
      }

      default: {
        return "Unavailable";
      }
    }
  }, [window, workerScore, windowThreshold, unmetCriteria]);

  function formatPill(label: string, variant: PillVariant, size: PillSize): React.ReactNode {
    return (
      <Pill
        variant={variant}
        size={size}
        label={label}
        iconType="hourglass"
        backgroundColor={(theme) => theme.palette.intent?.disabled.backgroundInvert}
        color={(theme) => theme.palette.text.secondary}
        iconColor={(theme) => theme.palette.text.secondary}
        sx={{
          width: "fit-content",
          position: "relative",
          zIndex: 3,
        }}
      />
    );
  }

  return (
    <>
      {formatPill(label, variant, size)}
      {/* This is to give the perception that the containing element is disabled. Not ideal, but it works. */}
      {disabled && (
        <Box
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 2,
          }}
        />
      )}
    </>
  );
}
